import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import Form from "@irati/ui/src/Form"
import GenericModal from "@irati/ui/src/GenericModal"
import {Body, H2, H3, H5} from "@irati/ui/src/Typography"
import {getCentValue, getNormalisedValue} from "@irati/utils/currency/formatCurrency"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {hangPromise} from "@irati/utils/promise"
import topupService from "@irati/wallet/services/TopUp"
import React, {FC, useEffect, useState, useMemo} from "react"
import {currencySchema, isDuration, State, useModalReducer} from "./modalReducer"
import Price from "./Price"
import ServiceFeeInfo from "./ServiceFeeInfo"
import {FlexGrow, Icon, StyledFlex, AmountInput, DurationSelect} from "./styled"

interface Props extends InjectIntl {
  isShown: boolean
  onHide: () => void
  onComplete: () => void
  children?: never
}

const SubscriptionModal: FC<Props> = ({onHide, isShown, onComplete, formatMessage}) => {
  const [state, dispatch] = useModalReducer()
  const {status, amount, rawUserAmount, annualAmount, duration} = state
  const [currencyCode, setCurrencyCode] = useState("")
  const options = useMemo(() => durationOptions(formatMessage), [formatMessage])

  const [serviceFeesRequest, setServiceFeesRequest] = useState(hangPromise())
  useEffect(() => {
    setServiceFeesRequest(
      topupService.serviceFeesTiers().then((feeTiers) => {
        dispatch({type: "setTiers", payload: feeTiers})
      })
    )

    topupService.getCurrencyCode().then(setCurrencyCode)
  }, [])

  return (
    <GenericModal onHide={onHide} className="modal-slide-up" show={isShown} hideActions>
      <Flex direction="column" gap={4}>
        <div />

        <Flex alignItems="center" justifyContent="flex-start" gap={2}>
          <Icon width={"55px"} />
          <H2>
            <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.title" />
          </H2>
        </Flex>

        <Body>
          <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.info" />
        </Body>

        <Form inline>
          <Flex direction="column" gap={2}>
            <H3>
              <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.input.label" />
            </H3>

            <Body>
              <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.input.info" />
            </Body>

            <Flex gap>
              <DurationSelect
                label={formatMessage({id: "wallet.reward.admin.subscriptionBanner.modalSubscribe.durationLabel"})}
                hideLabel
                isSearchable={false}
                isClearable={false}
                flush={true}
                onChange={(value) => {
                  if (isDuration(value)) {
                    dispatch({type: "setDuration", payload: value})
                  }
                }}
                value={options.find((o) => o.value === duration)}
                name="subscriptionDuration"
                placeholder={options[0].label}
                options={options}
              />

              <AmountInput
                label={formatMessage({id: "wallet.reward.admin.subscriptionBanner.modalSubscribe.input.label"})}
                hideLabel
                name="subscriptionAmount"
                type="number"
                validation={currencySchema}
                value={rawUserAmount ? getNormalisedValue(true, currencyCode, rawUserAmount).toString() : undefined}
                onChange={(value) => {
                  dispatch({type: "setAmount", payload: getCentValue(currencyCode, value)})
                }}
              />
            </Flex>

            {status !== "none" && (
              <StyledFlex>
                <FlexGrow as="span">
                  <H5>
                    <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.input.result" />
                  </H5>
                </FlexGrow>
                <FlexGrow as="span">
                  <Price value={annualAmount} currencyCode={currencyCode} state={status} />
                </FlexGrow>
              </StyledFlex>
            )}
          </Flex>
        </Form>

        {status === "none" ? (
          <div style={{height: "200px"}} />
        ) : (
          <ServiceFeeInfo state={state} currencyCode={currencyCode} serviceFeesRequest={serviceFeesRequest} />
        )}

        <Button
          disabled={status !== "valid"}
          primary
          full
          onClick={() => {
            tracking.clickSubscriptionConfirm(amount!, `${duration}ly`)
            onComplete()
          }}>
          <FormattedMessage id="wallet.reward.admin.subscriptionBanner.modalSubscribe.cta" />
        </Button>
      </Flex>
    </GenericModal>
  )
}

function durationOptions(formatMessage: InjectIntl["formatMessage"]): {label: string; value: State["duration"]}[] {
  return [
    {label: formatMessage({id: "wallet.reward.admin.subscriptionBanner.modalSubscribe.input.month"}), value: "month"},
    {label: formatMessage({id: "wallet.reward.admin.subscriptionBanner.modalSubscribe.input.quarter"}), value: "quarter"},
    {label: formatMessage({id: "wallet.reward.admin.subscriptionBanner.modalSubscribe.input.year"}), value: "year"}
  ]
}

export default injectIntl(SubscriptionModal)
